// [목적] 인증 필수 라우트 가드 — guest 시 /login?returnTo 로 redirect, loading 시 Skeleton
// [이유] fe-be-wire-up Wave 1 WC-FE-AUTH-06 — Spec R-5/R-8/R-T1 충족.
//         status==='loading' 동안 redirect 금지 (OAuthCallbackPage 무한루프 방지).
// [사이드 이팩트] requireOnboarded=true 시 authenticated + !isOnboarded → /onboarding 강제.
//                 loading 상태에서는 Skeleton 만 표시 — children 렌더 없음.
// [수정 시 고려사항] ProtectedRoute 는 authenticated 여부만 체크.
//                   onboarding 강제는 requireOnboarded prop 으로 명시적 opt-in.

import { Box, CircularProgress } from '@mui/material';
import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../providers/AuthProvider.js';
import { AUTH_COLORS } from '../../pages/auth/tokens.js';

interface ProtectedRouteProps {
  children: ReactNode;
  /** true 시 온보딩 미완료 사용자를 /onboarding 으로 강제 redirect */
  requireOnboarded?: boolean;
}

/**
 * [목적] 인증 필수 라우트 래퍼
 * [이유] WC-FE-AUTH-06: guest → /login?returnTo / loading → Skeleton / authenticated → children
 * [사이드 이팩트] requireOnboarded=true 면 !isOnboarded 시 /onboarding redirect
 * [수정 시 고려사항] status==='loading' 에서 절대 redirect 하지 않도록 유지 (R-5)
 */
export function ProtectedRoute({
  children,
  requireOnboarded = false,
}: ProtectedRouteProps): JSX.Element {
  const { status, isOnboarded } = useAuthContext();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: AUTH_COLORS.brandCream,
        }}
        role="status"
        aria-label="인증 확인 중"
      >
        <CircularProgress size={32} aria-hidden="true" sx={{ color: AUTH_COLORS.brandGold }} />
      </Box>
    );
  }

  if (status === 'guest') {
    return (
      <Navigate
        to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  // authenticated
  if (requireOnboarded && !isOnboarded) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
